import React, { useState } from "react";
import Papa from "papaparse";
import { useSelector } from "react-redux";
import apiInstance from "../../../utility/apiInstance";

const BulkUploadInstitutionsPage: React.FC = () => {
  const [csvData, setCsvData] = useState<any[]>([]);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [alert, setAlert] = useState<{ type: string; message: string } | null>(null);
  const [allErrors, setAllErrors] = useState<string[]>([]);
  const { user } = useSelector((state: any) => state.auth);

  // Download CSV Template
  const handleDownloadTemplate = () => {
    const headers = [
      "schoolName",
      "country",
      "phoneNumber",
      "about",
      "founded",
      "location",
      "schoolType",
    ] as const;

    type Header = (typeof headers)[number];

    const demoData: Record<Header, string>[] = [
      {
        schoolName: "Greenfield International School",
        country: "AE",
        phoneNumber: "+97145551234",
        about: "A leading IB curriculum school focusing on holistic education.",
        founded: "2007",
        location: "Dubai",
        schoolType: "International",
      },
      {
        schoolName: "Delhi Public School",
        country: "IN",
        phoneNumber: "+911122334455",
        about: "One of India's most reputed CBSE schools with 50+ branches.",
        founded: "1998",
        location: "New Delhi",
        schoolType: "CBSE",
      },
      {
        schoolName: "British School of Doha",
        country: "QA",
        phoneNumber: "+97444112233",
        about: "British curriculum school offering IGCSE and A-Levels.",
        founded: "2003",
        location: "Doha",
        schoolType: "British",
      },
    ];

    const csv = Papa.unparse({
      fields: [...headers],
      data: demoData.map((item) => headers.map((key) => item[key])),
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "institutions_template.csv";
    a.click();
  };

  // Handle CSV Upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const cleaned = results.data.map((row: any) => {
          const cleanedRow: any = {};
          Object.keys(row).forEach((key) => {
            cleanedRow[key.trim()] = (row[key] || "").toString().trim();
          });
          return cleanedRow;
        });
        setCsvData(cleaned);
        setPreviewData(cleaned.slice(0, 5));
        setAllErrors([]);
        setAlert(null);
      },
    });
  };

  // Validation
  const validateInstitution = (inst: any, index: number) => {
    const errors: string[] = [];

    if (!inst.schoolName) errors.push(`Row ${index + 1}: Missing schoolName`);
    if (!inst.country) errors.push(`Row ${index + 1}: Missing country`);
    if (inst.founded && isNaN(Number(inst.founded)))
      errors.push(`Row ${index + 1}: Invalid founded year`);
    if (inst.phoneNumber && !/^[0-9+\-\s]{5,20}$/.test(inst.phoneNumber))
      errors.push(`Row ${index + 1}: Invalid phone number`);

    return errors;
  };

  // Bulk Upload Handler
  const handleBulkUpload = async () => {
    if (!csvData.length) {
      setAlert({ type: "danger", message: "Please upload a CSV file first." });
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setAllErrors([]);

    const total = csvData.length;
    let successCount = 0;
    let errorMessages: string[] = [];

    for (let i = 0; i < total; i++) {
      const inst = csvData[i];
      const validationErrors = validateInstitution(inst, i);

      if (validationErrors.length > 0) {
        errorMessages.push(...validationErrors);
        setProgress(Math.round(((i + 1) / total) * 100));
        continue;
      }

      try {
        await apiInstance.post("/api/institutions", {
          schoolName: inst.schoolName,
          country: inst.country,
          phoneNumber: inst.phoneNumber || "",
          about: JSON.stringify({
            root: {
              type: "root",
              children: [
                {
                  type: "paragraph",
                  children: [
                    {
                      type: "text",
                      text: inst.about || "",
                    },
                  ],
                  direction: "ltr",
                  format: "",
                  indent: 0,
                  version: 1,
                },
              ],
              direction: "ltr",
              format: "",
              indent: 0,
              version: 1,
            },
          }),
          founded: inst.founded ? Number(inst.founded) : null,
          location: inst.location || "",
          schoolType: inst.schoolType || "",
        });

        successCount++;
      } catch (err: any) {
        const msg =
          err?.response?.data?.errors?.[0]?.message ||
          err.message ||
          "Unknown error while uploading institution";

        errorMessages.push(`Row ${i + 1}: ${msg}`);
      }

      setProgress(Math.round(((i + 1) / total) * 100));
    }

    setIsUploading(false);
    setAllErrors(errorMessages);

    if (errorMessages.length > 0) {
      setAlert({
        type: "warning",
        message: `Completed with ${successCount}/${total} successful uploads. ${errorMessages.length} errors found.`,
      });
    } else {
      setAlert({
        type: "success",
        message: `All ${successCount} institutions uploaded successfully!`,
      });
    }
  };

  return (
    <div className="d-flex flex-column align-items-center min-vh-100 bg-light p-5">
      <div className="container bg-white p-5 rounded shadow w-100" style={{ maxWidth: "1100px" }}>
        <h1 className="text-center mb-4 fw-semibold fs-2">Bulk Upload Institutions</h1>

        {alert && (
          <div className={`alert alert-${alert.type}`} role="alert">
            {alert.message}
          </div>
        )}

        <div className="mb-5">
          <div className="d-flex flex-column align-items-start gap-3 mb-4">
            <button
              onClick={handleDownloadTemplate}
              className="btn btn-primary px-4 py-2"
            >
              Download CSV Template
            </button>

            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="form-control w-50"
            />
          </div>

          {previewData.length > 0 && (
            <div>
              <h5 className="fw-medium mb-2">Preview (first 5 rows):</h5>
              <div className="table-responsive">
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      {Object.keys(previewData[0]).map((key) => (
                        <th key={key}>{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, idx) => (
                      <tr key={idx}>
                        {Object.values(row).map((val: any, j) => (
                          <td key={j}>{val}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="text-center mt-4">
            <button
              onClick={handleBulkUpload}
              disabled={isUploading}
              className={`btn btn-lg text-white fw-medium ${
                isUploading ? "btn-secondary" : "btn-success"
              }`}
            >
              {isUploading ? `Uploading... ${progress}%` : "Start Bulk Upload"}
            </button>
          </div>

          {/* Scrollable Error Section */}
          {allErrors.length > 0 && (
            <div className="mt-5">
              <h5 className="text-danger fw-semibold mb-3">
                Upload Errors ({allErrors.length})
              </h5>

              <div
                className="border rounded p-3 bg-light"
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  fontSize: "13px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {allErrors.map((err, index) => (
                  <div key={index} className="text-danger mb-1">
                    {err}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadInstitutionsPage;
